import React, { useState } from "react";
import { MicrophoneIcon, StopIcon } from "@heroicons/react/20/solid";

const SpeechToText = ({ meeting }) => {
  const [text, setText] = useState("");
  const [isListening, setIsListening] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [updatemeeting, setUpdatemeeting] = useState(null);

  // Speech recognition logic
  const handleListen = () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      alert("Speech Recognition is not supported in your browser.");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    if (isListening) {
      recognition.stop();
      setIsListening(false);
    } else {
      recognition.start();
      setIsListening(true);

      recognition.onresult = (event) => {
        let interimTranscript = "";

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;

          if (event.results[i].isFinal) {
            setText((prev) => prev + transcript + " ");
          } else {
            interimTranscript += transcript;
          }
        }
      };

      recognition.onerror = (event) => {
        console.error("Error in speech recognition:", event.error);
        setIsListening(false);
      };

      recognition.onend = () => {
        console.log("Speech recognition stopped.");
        setIsListening(false);
      };
    }
  };

  // Function to get the last 4 lines of the text
  const getLastLines = (text, numberOfLines) => {
    const lines = text.split(/\n|\. /).filter((line) => line.trim() !== "");
    return lines.slice(-numberOfLines).join(". ") + (lines.length > numberOfLines ? "." : "");
  };

  // Save the raw meeting notes
  const handleSave = async () => {
    if (!text) {
      alert("No text available to save!");
      return;
    }

    try {
      const savedMeeting = {
        id: meeting.id,
        MeetingNotes: text,
      };

      const response = await fetch("https://api.insightos.co.uk/api/savemeetingnotes", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(savedMeeting),
      });

      if (!response.ok) {
        throw new Error("Failed to save meeting notes");
      }

      console.log("Meeting notes saved successfully!");
      setUpdatemeeting("Meeting notes saved successfully!");
    } catch (error) {
      console.error("Error saving meeting notes:", error);
    }
  };

  return (
    <div className="w-full px-3 sm:px-6 lg:px-8 min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900">
      <div className="max-w-2xl mx-auto p-4 sm:p-6 bg-gradient-to-br from-gray-800/90 via-blue-800/90 to-purple-800/90 backdrop-blur-lg border border-cyan-500/30 rounded-2xl shadow-2xl shadow-cyan-500/20">
        <h1 className="text-base sm:text-xl font-bold mb-3 sm:mb-4 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400 flex items-center">
          <span className="mr-2 text-cyan-400">🤖</span> AI Minutes Taker
        </h1>

        <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-black/50 border border-cyan-500/40 rounded-xl shadow-inner backdrop-blur-sm">
        <h2 className="font-semibold text-cyan-300 text-xs sm:text-base mb-2 flex items-center">
          <span className="w-2 h-2 bg-cyan-400 rounded-full mr-2 animate-pulse"></span>
          Transcript
        </h2>
        <div className="relative">
          <p className="text-gray-100 mt-2 text-xs sm:text-sm leading-relaxed break-words font-mono bg-gray-900/60 p-3 rounded-lg border border-cyan-500/20 min-h-[80px] max-h-[200px] overflow-y-auto scrollbar-thin scrollbar-thumb-cyan-500/50 scrollbar-track-gray-800/50">
            {text ? (isExpanded ? text : getLastLines(text, 4)) : (
              <span className="text-gray-500 italic">Awaiting input...</span>
            )}
          </p>
          {text && (
            <div className="absolute top-1 right-1">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
            </div>
          )}
        </div>
        {text.split(/\n|\. /).length > 4 && (
          <button
            onClick={() => setIsExpanded((prev) => !prev)}
            className="mt-2 text-cyan-400 hover:text-cyan-300 text-xs sm:text-sm transition-all duration-300 hover:scale-105 font-medium"
          >
            {isExpanded ? "↑ Collapse" : "↓ Expand Data"}
          </button>
        )}
        <button
          onClick={handleListen}
          className={`relative flex items-center p-3 sm:p-4 justify-center py-3 sm:py-4 rounded-xl font-semibold transition-all duration-300 mt-4 w-full border-2 group overflow-hidden ${
            isListening 
              ? "bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-500 hover:to-pink-500 border-red-400/50 shadow-lg shadow-red-500/25" 
              : "bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 border-cyan-400/50 shadow-lg shadow-cyan-500/25"
          }`}
        >
          <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          {isListening ? (
            <>
              <StopIcon className="h-4 w-4 sm:h-6 sm:w-6 text-white mr-2 relative z-10" />
              <span className="text-xs sm:text-base relative z-10">TERMINATE  LINK</span>
              <div className="absolute right-2 flex space-x-1">
                <div className="w-1 h-1 bg-red-300 rounded-full animate-bounce"></div>
                <div className="w-1 h-1 bg-red-300 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                <div className="w-1 h-1 bg-red-300 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
              </div>
            </>
          ) : (
            <>
              <MicrophoneIcon className="h-4 w-4 sm:h-6 sm:w-6 text-white mr-2 relative z-10" />
              <span className="text-xs sm:text-base relative z-10">ACTIVATE LINK</span>
            </>
          )}
        </button>
      </div>

      {text && (
        <div className="mt-4">
          <button
            onClick={handleSave}
            data-component="meeting-note-recorder"
            data-time-saved="60"
            data-feature="save_meeting_notes_put"
            className="relative w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white py-3 sm:py-4 rounded-xl transition-all duration-300 text-xs sm:text-base font-bold border-2 border-green-400/50 shadow-lg shadow-green-500/25 group overflow-hidden"
          >
            <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative z-10 flex items-center justify-center">
              <span className="mr-2">💾</span>
              ARCHIVE DATA
            </div>
          </button>
        </div>
      )}

      {updatemeeting && (
        <div className="mt-4 p-3 bg-green-900/50 border border-green-500/30 rounded-xl backdrop-blur-sm">
          <p className="text-green-300 text-xs sm:text-sm text-center flex items-center justify-center">
            <span className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></span>
            {updatemeeting}
          </p>
        </div>
      )}
      </div>
    </div>
  );
};

export default SpeechToText;